import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import BackgroundIcons from '../../components/BackgroundIcons';
import NotificationModal from '../../components/NotificationModal';
import { apiService } from '../../services/apiService';
import { getLoginRequiredNotification } from '../../utils/notificationHelpers';
import { saveRitualToWeddingPlan } from '../../utils/ritualHelpers';

const CulturalRitualExplainer = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCulture, setSelectedCulture] = useState('');
  const [rituals, setRituals] = useState([]);
  const [cultures, setCultures] = useState([{ name: 'All Cultures', value: '' }]);
  const [loading, setLoading] = useState(true);
  const [isVisible, setIsVisible] = useState(false);
  const [expandedRitual, setExpandedRitual] = useState(null);
  const [savedRitualIds, setSavedRitualIds] = useState(new Set());
  const [ritualEntryPoint, setRitualEntryPoint] = useState('direct');
  const [notification, setNotification] = useState({
    show: false,
    title: '',
    message: '',
    type: 'info'
  });

  // Fetch cultures and initial rituals on mount
  useEffect(() => {
    const init = async () => {
      await fetchCultures();
      await fetchAllRituals();
      await loadSavedRituals();
      setIsVisible(true);
      
      // Track ritual conversion (page load) - dedup per theme ref, matching vendor funnel pattern
      const params = new URLSearchParams(window.location.search);
      let from = params.get('from') || 'direct';
      const ref = params.get('ref') || '';
      if (from !== 'direct') {
        const convKey = `ritual_conv_${ref || from}`;
        if (localStorage.getItem(convKey)) {
          from = 'direct';
        } else {
          localStorage.setItem(convKey, '1');
        }
        window.history.replaceState({}, '', window.location.pathname);
      }
      setRitualEntryPoint(from);
      
      try {
        await apiService.feedback.trackRitualConversion({
          entry_point: from
        });
      } catch (error) {
        console.error('Error tracking ritual conversion:', error);
      }
    };
    init();
  }, []);

  // Fetch rituals when culture changes
  useEffect(() => {
    if (selectedCulture) {
      fetchRitualsByCulture(selectedCulture);
    } else {
      fetchAllRituals();
    }
  }, [selectedCulture]);

  // Auto-search when query changes (debounced)
  useEffect(() => {
    if (searchQuery) {
      const debounceTimer = setTimeout(() => {
        handleSearch();
      }, 500);
      return () => clearTimeout(debounceTimer);
    }
  }, [searchQuery]);

  const fetchCultures = async () => {
    try {
      const response = await apiService.rituals.getCultures();
      if (response.success) {
        const cultureOptions = [
          { name: 'All Cultures', value: '' },
          ...response.cultures.map(c => ({
            name: c.name,
            value: c.id
          }))
        ];
        setCultures(cultureOptions);
      }
    } catch (error) {
      console.error('Error fetching cultures:', error);
    }
  };

  const fetchAllRituals = async () => {
    setLoading(true);
    try {
      const response = await apiService.rituals.getCultures();
      if (response.success) {
        // Fetch rituals for all cultures
        const allRituals = [];
        for (const culture of response.cultures) {
          const cultureResponse = await apiService.rituals.getByCulture(culture.id);
          if (cultureResponse.success) {
            allRituals.push(...cultureResponse.rituals);
          }
        }
        setRituals(allRituals);
      }
    } catch (error) {
      console.error('Error fetching rituals:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchRitualsByCulture = async (culture) => {
    setLoading(true);
    try {
      const response = await apiService.rituals.getByCulture(culture);
      if (response.success) {
        setRituals(response.rituals);
      }
    } catch (error) {
      console.error('Error fetching rituals:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = async () => {
    if (!searchQuery.trim()) {
      if (selectedCulture) {
        fetchRitualsByCulture(selectedCulture);
      } else {
        fetchAllRituals();
      }
      return;
    }

    setLoading(true);
    try {
      const params = {
        query: searchQuery,
        limit: 20
      };
      if (selectedCulture) {
        params.culture = selectedCulture;
      }
      
      const response = await apiService.rituals.search(params);
      if (response.success) {
        setRituals(response.rituals);
      }
    } catch (error) {
      console.error('Error searching rituals:', error);
    } finally {
      setLoading(false);
    }
  };

  const toggleRitual = (ritualId) => {
    setExpandedRitual(expandedRitual === ritualId ? null : ritualId);
  };

  const loadSavedRituals = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) return;
      
      const response = await apiService.userRituals.getAll();
      const savedIds = new Set(response.rituals.map(r => r.ritual_id));
      setSavedRitualIds(savedIds);
    } catch (error) {
      console.error('Error loading saved rituals:', error);
    }
  };

  const addToWeddingPlan = async (ritual) => {
    const result = await saveRitualToWeddingPlan(ritual, ritualEntryPoint);
    if (result.success) setSavedRitualIds(prev => new Set([...prev, ritual.id]));
    setNotification(result.notification);
  };

  const getEmojiForCulture = (culture) => {
    const emojiMap = {
      'western': '💍',
      'indian': '🔥',
      'chinese': '🍵',
      'jewish': '✡️',
      'african': '🥁',
      'islamic': '☪️',
      'japanese': '🍶',
      'korean': '🎎',
      'latin_american': '💃',
      'celtic': '☘️',
      'southeast_asian': '🌺',
      'modern': '✨'
    };
    return emojiMap[culture] || '🎊';
  };

  const clearSearch = () => {
    setSearchQuery('');
    setSelectedCulture('');
    fetchAllRituals();
  };

  const pageIcons = [
    { emoji: '📖', size: '4xl', opacity: '15' },
    { emoji: '🌍', size: '4xl', opacity: '15' },
    { emoji: '🕯️', size: '4xl', opacity: '15' },
    { emoji: '🎭', size: '4xl', opacity: '15' }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-rose-50 via-pink-25 to-champagne-50 relative overflow-hidden">
      <BackgroundIcons icons={pageIcons} />
      
      {/* Animated Background Elements */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        {/* Floating sparkles */}
        {[...Array(15)].map((_, i) => (
          <div
            key={i}
            className="absolute animate-pulse opacity-10"
            style={{
              left: `${Math.random() * 100}%`,
              top: `${Math.random() * 100}%`,
              animationDelay: `${Math.random() * 3}s`,
              animationDuration: `${2 + Math.random() * 2}s`
            }}
          >
            <span className="text-gold-400 text-lg">✨</span>
          </div>
        ))}
      </div>

      <div className="max-w-6xl mx-auto px-6 py-12 relative z-10">
        
        {/* Elegant Header */}
        <div className={`text-center mb-16 transition-all duration-1000 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
          <div className="inline-flex items-center justify-center w-20 h-20 bg-gradient-to-r from-rose-400 via-pink-400 to-gold-400 rounded-full shadow-2xl mb-8 animate-bounce">
            <div className="relative">
              <span className="text-2xl">📖</span>
              <span className="absolute -top-1 -right-1 text-lg animate-pulse">🌍</span>
            </div>
          </div>
          
          <h1 className="text-6xl md:text-7xl font-serif font-bold bg-gradient-to-r from-rose-600 via-pink-600 to-gold-600 bg-clip-text text-transparent mb-6 tracking-wide">
            Cultural Ritual Explainer
          </h1>
          
          {/* Floral underline */}
          <div className="flex justify-center mb-8">
            <div className="relative">
              <div className="w-32 h-1 bg-gradient-to-r from-transparent via-gold-400 to-transparent rounded-full"></div>
              <div className="absolute -top-2 left-1/2 transform -translate-x-1/2 text-gold-400">💖</div>
            </div>
          </div>
          
          <p className="text-xl text-gray-700 max-w-3xl mx-auto leading-relaxed font-light">
            Discover beautiful wedding traditions from around the world and incorporate meaningful rituals into your special day
          </p>
        </div>

        {/* Search Section */}
        <div className={`mb-12 transition-all duration-700 delay-200 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
          <div className="bg-white/70 backdrop-blur-lg rounded-3xl shadow-2xl border-2 border-gold-200/50 p-8">
            <div className="flex items-center mb-6">
              <div className="w-12 h-12 bg-gradient-to-r from-purple-400 to-pink-400 rounded-2xl flex items-center justify-center mr-4 shadow-lg animate-pulse">
                <span className="text-2xl">🔍</span>
              </div>
              <h3 className="text-2xl font-serif font-bold bg-gradient-to-r from-purple-600 to-pink-600 bg-clip-text text-transparent">
                Explore Cultural Traditions
              </h3>
            </div>
            
            <div className="grid md:grid-cols-3 gap-6">
              {/* Search Bar */}
              <div className="md:col-span-2 relative">
                <div className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gold-400">
                  <span className="text-xl">🌍</span>
                </div>
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Search for rituals, traditions, or ceremonies..."
                  className="w-full pl-12 pr-4 py-4 border-2 border-gold-200 rounded-xl focus:ring-4 focus:ring-gold-200 focus:border-gold-400 transition-all duration-300 bg-white/80 font-medium"
                />
              </div>

              {/* Culture Filter */}
              <div className="relative">
                <select
                  value={selectedCulture}
                  onChange={(e) => setSelectedCulture(e.target.value)}
                  className="w-full px-4 py-4 border-2 border-gold-200 rounded-xl focus:ring-4 focus:ring-gold-200 focus:border-gold-400 transition-all duration-300 bg-white/80 font-medium appearance-none"
                >
                  {cultures.map((culture) => (
                    <option key={culture.value} value={culture.value}>
                      {culture.name}
                    </option>
                  ))}
                </select>
                <div className="absolute right-4 top-1/2 transform -translate-y-1/2 text-gold-400 pointer-events-none">
                  <span className="text-xl">🎭</span>
                </div>
              </div>
            </div>

            {(searchQuery || selectedCulture) && (
              <div className="mt-4 flex justify-center">
                <button
                  onClick={clearSearch}
                  className="px-6 py-2 border-2 border-gray-300 text-gray-600 rounded-xl font-medium hover:bg-gray-50 transition-all duration-300"
                >
                  Clear Search
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Results Section */}
        <div className={`transition-all duration-700 delay-400 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
          {loading ? (
            <div className="text-center py-20">
              <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-rose-500 mx-auto mb-4"></div>
              <p className="text-xl text-gray-600">Discovering beautiful traditions...</p>
            </div>
          ) : (
            <div className="space-y-6">
              <div className="flex items-center justify-between mb-8">
                <h3 className="text-2xl font-serif font-bold text-gray-800">
                  {rituals.length} Cultural {rituals.length === 1 ? 'Ritual' : 'Rituals'} Found
                </h3>
                
                {/* View My Saved Rituals Button - Only for logged in users */}
                {localStorage.getItem('token') && savedRitualIds.size > 0 && (
                  <Link to="/dashboard/rituals">
                    <button className="group relative overflow-hidden bg-gradient-to-r from-purple-500 via-pink-500 to-gold-500 text-white py-2 px-6 rounded-xl font-bold text-sm hover:shadow-xl transform hover:scale-105 transition-all duration-300">
                      <span className="relative z-10 flex items-center">
                        <span className="mr-2">🎭</span>
                        View My Rituals
                        <span className="ml-2 px-2 py-1 bg-white/20 rounded-full text-xs">
                          {savedRitualIds.size}
                        </span>
                      </span>
                    </button>
                  </Link>
                )}
              </div>

              {rituals.map((ritual, index) => (
                <div
                  key={ritual.id}
                  className="bg-white/80 backdrop-blur-sm rounded-3xl shadow-xl border-2 border-gold-200/50 overflow-hidden hover:shadow-2xl transition-all duration-500 animate-fadeInScale"
                  style={{ animationDelay: `${index * 150}ms` }}
                >
                  {/* Ritual Header */}
                  <div
                    className="p-8 cursor-pointer hover:bg-gradient-to-r hover:from-white/90 hover:to-rose-50/90 transition-all duration-300"
                    onClick={() => toggleRitual(ritual.id)}
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex items-start space-x-6">
                        {/* Cultural Image */}
                        <div className="flex-shrink-0">
                          <div className="w-20 h-20 bg-gradient-to-br from-gold-100 to-yellow-200 rounded-2xl flex items-center justify-center shadow-lg border-2 border-gold-300">
                            <span className="text-4xl">{getEmojiForCulture(ritual.culture)}</span>
                          </div>
                        </div>

                        {/* Ritual Info */}
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center space-x-3 mb-2">
                            <h4 className="text-2xl font-serif font-bold text-gray-800">
                              {ritual.name}
                            </h4>
                            <span className="px-3 py-1 bg-gradient-to-r from-purple-100 to-pink-100 text-purple-700 rounded-full text-sm font-semibold border border-purple-200">
                              {ritual.culture_display_name}
                            </span>
                            {searchQuery && ritual.similarity && (
                              <span className="px-3 py-1 bg-gradient-to-r from-green-100 to-emerald-100 text-green-700 rounded-full text-sm font-semibold border border-green-200">
                                {Math.round(ritual.similarity * 100)}% match
                              </span>
                            )}
                          </div>
                          <p className="text-gray-600 leading-relaxed break-words">
                            {ritual.description}
                          </p>
                        </div>
                      </div>

                      {/* Expand Icon */}
                      <div className={`flex-shrink-0 ml-4 transform transition-transform duration-300 ${expandedRitual === ritual.id ? 'rotate-180' : ''}`}>
                        <span className="text-2xl text-gold-400">⌄</span>
                      </div>
                    </div>
                  </div>

                  {/* Expanded Content */}
                  {expandedRitual === ritual.id && (
                    <div className="px-8 pb-8 animate-fadeIn">
                      <div className="bg-gradient-to-br from-rose-50/80 to-pink-50/80 rounded-2xl p-6 border border-rose-200/50">
                        <div className="grid md:grid-cols-2 gap-6">
                          <div>
                            <h5 className="text-lg font-bold text-gray-800 mb-3">Full Description</h5>
                            <p className="text-gray-700 leading-relaxed mb-4 break-words">
                              {ritual.meaning}
                            </p>
                            
                            {ritual.steps && ritual.steps.length > 0 && (
                              <>
                                <h5 className="text-lg font-bold text-gray-800 mb-2">Steps</h5>
                                <ol className="list-decimal ml-5 space-y-1 text-gray-700">
                                  {ritual.steps.map((step, idx) => (
                                    <li key={idx}>{step}</li>
                                  ))}
                                </ol>
                              </>
                            )}
                          </div>
                          
                          <div>
                            <h5 className="text-lg font-bold text-gray-800 mb-3">Ritual Details</h5>
                            <div className="space-y-3">
                              <div className="flex items-center space-x-3">
                                <span className="text-lg">⏱️</span>
                                <span className="text-gray-700">Duration: {ritual.duration_minutes} minutes</span>
                              </div>
                              
                              <div className="flex items-center space-x-3">
                                <span className="text-lg">📊</span>
                                <span className="text-gray-700">Complexity: {ritual.complexity}</span>
                              </div>

                              <div className="flex items-center space-x-3">
                                <span className="text-lg">👔</span>
                                <span className="text-gray-700">Formality: {ritual.formality}</span>
                              </div>
                              
                              {ritual.items_needed && ritual.items_needed.length > 0 && (
                                <div>
                                  <div className="flex items-center space-x-3 mb-2">
                                    <span className="text-lg">📋</span>
                                    <span className="font-semibold text-gray-700">Items Needed:</span>
                                  </div>
                                  <ul className="ml-8 space-y-1">
                                    {ritual.items_needed.map((item, idx) => (
                                      <li key={idx} className="text-gray-600 flex items-center">
                                        <span className="text-gold-400 mr-2">•</span>
                                        {item}
                                      </li>
                                    ))}
                                  </ul>
                                </div>
                              )}

                              {ritual.modern_variations && ritual.modern_variations.length > 0 && (
                                <div>
                                  <div className="flex items-center space-x-3 mb-2">
                                    <span className="text-lg">✨</span>
                                    <span className="font-semibold text-gray-700">Modern Variations:</span>
                                  </div>
                                  <ul className="ml-8 space-y-1">
                                    {ritual.modern_variations.map((variation, idx) => (
                                      <li key={idx} className="text-gray-600 flex items-center">
                                        <span className="text-gold-400 mr-2">•</span>
                                        {variation}
                                      </li>
                                    ))}
                                  </ul>
                                </div>
                              )}
                            </div>
                          </div>
                        </div>

                        {/* Add to Wedding Plan Button */}
                        <div className="mt-6 text-center">
                          <button
                            onClick={() => addToWeddingPlan(ritual)}
                            disabled={savedRitualIds.has(ritual.id)}
                            className={`px-8 py-3 rounded-xl font-bold transform hover:scale-105 transition-all duration-300 ${
                              savedRitualIds.has(ritual.id)
                                ? 'bg-gradient-to-r from-green-500 to-emerald-500 text-white cursor-default'
                                : 'bg-gradient-to-r from-rose-500 to-gold-500 text-white hover:from-rose-600 hover:to-gold-600 hover:shadow-xl'
                            }`}
                          >
                            <span className="mr-2">{savedRitualIds.has(ritual.id) ? '✓' : '📅'}</span>
                            {savedRitualIds.has(ritual.id) ? 'Added to Collection' : 'Add to Wedding Plan'}
                          </button>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              ))}

              {rituals.length === 0 && (
                <div className="text-center py-20">
                  <div className="text-6xl mb-4">🔍</div>
                  <p className="text-xl text-gray-600 mb-4">No rituals found matching your search</p>
                  <button
                    onClick={clearSearch}
                    className="bg-gradient-to-r from-rose-500 to-gold-500 text-white px-8 py-3 rounded-xl font-bold hover:shadow-xl transition-all duration-300"
                  >
                    View All Rituals
                  </button>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Luxury Footer */}
        <div className={`mt-16 text-center transition-all duration-700 delay-600 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
          <div className="w-32 h-px bg-gradient-to-r from-transparent via-gold-400 to-transparent mx-auto mb-4"></div>
          <p className="text-gray-600 font-light italic">
            Part of your personalized VowAI Wedding Journey
          </p>
        </div>
      </div>

      <style jsx>{`
        @keyframes fadeInScale {
          from {
            opacity: 0;
            transform: scale(0.8);
          }
          to {
            opacity: 1;
            transform: scale(1);
          }
        }
        
        @keyframes fadeIn {
          from {
            opacity: 0;
            transform: translateY(20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        
        .animate-fadeInScale {
          animation: fadeInScale 0.6s ease-out;
        }
        
        .animate-fadeIn {
          animation: fadeIn 0.4s ease-out;
        }
        
        .delay-200 { animation-delay: 200ms; }
        .delay-400 { animation-delay: 400ms; }
        .delay-600 { animation-delay: 600ms; }
        .delay-1000 { animation-delay: 1000ms; }
        .delay-1500 { animation-delay: 1500ms; }
        .delay-2000 { animation-delay: 2000ms; }
      `}</style>

      {/* Notification Modal */}
      <NotificationModal
        isOpen={notification.show}
        onClose={() => setNotification({ ...notification, show: false })}
        type={notification.type}
      actionLabel={notification.actionLabel}
      onAction={notification.onAction}
        title={notification.title}
        message={notification.message}
      />
    </div>
  );
};

export default CulturalRitualExplainer;
